import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Search, Details, TableHeader, AboutNode } from '@/components'
import { driversPath, makeSearchPath, sort } from '@/utils'
import { t } from '@/i18n'

import EditDriver from './components/EditDriver'
import DriverRow from './components/DriverRow'
import { useSelect } from './useSelect'
import { buildData } from './utils'
import { useData } from './useData'
import { OrderType } from '@/types'

const columns = [
  {
    key: 'id',
    sortKey: 'id',
  },
  {
    key: 'name',
    sortKey: 'name',
  },
]

const Drivers: React.FC = () => {
  const { id, customerId } = useParams()
  const navigate = useNavigate()
  const _customerId = useMemo(
    () => (customerId === 'customer' ? undefined : Number(customerId)),
    [customerId],
  )
  const [sortKey, setKey] = useState<string>()
  const [orderBy, setOrderBy] = useState<OrderType>()
  const [visible, setVisible] = useState(false)
  const { data, loading, onFetchMore, onSearch, pageInfo } =
    useSelect(_customerId)
  const { data: selectedData, onDelete, loading: carsLoading } = useData({ id })

  const HeaderComponent = useMemo(
    () => <Search onSearch={onSearch} />,
    [onSearch],
  )

  useEffect(() => {
    setVisible(false)
    setKey(undefined)
    setOrderBy(undefined)
  }, [id])

  const onClose = useCallback(() => {
    navigate(makeSearchPath(driversPath, { customerId: _customerId }))
  }, [navigate, _customerId])

  const sortingBy = useCallback((key?: string, by?: OrderType) => {
    setKey(key)
    setOrderBy(by)
  }, [])

  return (
    <div className="row">
      <div className={`col w-${!id ? '100' : '45'}`}>
        <Details
          showNoData
          Wrapper="table"
          RowParent="tbody"
          Row={DriverRow}
          TitleWrapper="h4"
          loading={loading}
          title={t('drivers')}
          onFetchMore={onFetchMore}
          listWrapperClassName="list-items"
          wrapperClassName="info-box w-100"
          HeaderComponent={HeaderComponent}
          dataBoxWrapperClassName="info-table"
          fetchMoreStatus={pageInfo.fetchMoreStatus}
          Header={() => (
            <TableHeader
              columns={columns}
              onSort={sortingBy}
              orderedBy={orderBy}
            />
          )}
          data={data.sort((a, b) => sort(a, b, sortKey, orderBy))}
        />
      </div>
      <div className="col w-55">
        <AboutNode
          loading={carsLoading}
          sections={[
            {
              title: 'personal_info',
              data: buildData(selectedData),
              headerButtons: [
                {
                  children: (
                    <>
                      <span className="icon-delete"></span>
                      {t('delete')}
                    </>
                  ),
                  className: 'main-btn red',
                  onClick: () => {
                    onDelete(onClose)
                  },
                },
                {
                  children: (
                    <>
                      <span className="icon-edit"></span>
                      {t('edit')}
                    </>
                  ),
                  className: 'main-btn outline',
                  onClick: () => setVisible(true),
                },
              ],
            },
          ]}
          onClose={onClose}
          node={visible ? undefined : selectedData}
        />
        <EditDriver
          visible={visible}
          node={selectedData}
          onClose={() => setVisible(false)}
        />
      </div>
    </div>
  )
}

export default Drivers
